/**
 * labelDesign.ts — Modelo de diseño de la etiqueta masiva de productos.
 *
 * QUÉ HACE:
 *  - Define `LabelDesign` (contenido + estructura + estilo), las plantillas base
 *    (cada una con una ESTRUCTURA distinta, no solo color), las paletas de color
 *    y el cálculo del precio con descuento de catálogo.
 *  - Normaliza diseños leídos de presets para tolerar datos incompletos.
 *
 * QUÉ EXPORTA: tipos, DEFAULT_LABEL_DESIGN, LABEL_TEMPLATES, LABEL_PALETTES,
 *              LABEL_LAYOUTS, LABEL_FONT_STACKS, LABEL_SIZE_FACTORS,
 *              designFromTemplate, normalizeLabelDesign, resolveLabelPrice.
 *
 * QUÉ NO HACE: no renderiza, no imprime, no toca persistencia.
 */

/** Producto mínimo que necesita una etiqueta (nombre, precio y descuento). */
export interface LabelProduct {
  nombre: string;
  precio: number;
  descuento?: number;
}

/** Estructura visual de la etiqueta (dónde va cada bloque). */
export type LabelLayoutId =
  | "centrado"
  | "banda"
  | "split"
  | "precioGrande"
  | "ficha"
  | "minimal";

export type LabelTemplateId =
  | "clasico"
  | "oferta"
  | "estante"
  | "precio"
  | "ficha"
  | "minimal";

export type LabelFontFamily = "sans" | "serif" | "mono";

export type LabelTextSize = "compacto" | "normal" | "grande";

export type LabelOfferPosition = "top" | "badge" | "bottom";

export interface LabelDesign {
  templateId: LabelTemplateId;
  layout: LabelLayoutId;
  showBusiness: boolean;
  showName: boolean;
  showOldPrice: boolean;
  showOffer: boolean;
  offerText: string;
  offerPosition: LabelOfferPosition;
  fontFamily: LabelFontFamily;
  textSize: LabelTextSize;
  uppercaseName: boolean;
  showDivider: boolean;
  showBorder: boolean;
  rounded: boolean;
  bgColor: string;
  textColor: string;
  priceColor: string;
  offerBgColor: string;
  offerTextColor: string;
  borderColor: string;
}

const BASE_DESIGN: LabelDesign = {
  templateId: "clasico",
  layout: "centrado",
  showBusiness: false,
  showName: true,
  showOldPrice: true,
  showOffer: false,
  offerText: "",
  offerPosition: "badge",
  fontFamily: "sans",
  textSize: "normal",
  uppercaseName: true,
  showDivider: true,
  showBorder: true,
  rounded: true,
  bgColor: "#FFFFFF",
  textColor: "#0F172A",
  priceColor: "#0F172A",
  offerBgColor: "#DC2626",
  offerTextColor: "#FFFFFF",
  borderColor: "#CBD5E1",
};

export const DEFAULT_LABEL_DESIGN: LabelDesign = { ...BASE_DESIGN };

function makeDesign(overrides: Partial<LabelDesign>): LabelDesign {
  return { ...BASE_DESIGN, ...overrides };
}

export interface LabelTemplate {
  id: LabelTemplateId;
  nombre: string;
  descripcion: string;
  design: LabelDesign;
}

export const LABEL_TEMPLATES: LabelTemplate[] = [
  {
    id: "clasico",
    nombre: "Clásico",
    descripcion: "Nombre y precio centrados sobre fondo blanco",
    design: makeDesign({ templateId: "clasico", layout: "centrado" }),
  },
  {
    id: "oferta",
    nombre: "Oferta",
    descripcion: "Banda de color con el precio grande y el mensaje de oferta",
    design: makeDesign({
      templateId: "oferta",
      layout: "banda",
      bgColor: "#FFFFFF",
      textColor: "#7F1D1D",
      priceColor: "#DC2626",
      offerBgColor: "#DC2626",
      offerTextColor: "#FFFFFF",
      borderColor: "#FECACA",
    }),
  },
  {
    id: "estante",
    nombre: "Estante",
    descripcion: "Bloque dividido: nombre arriba, precio sobre color abajo",
    design: makeDesign({
      templateId: "estante",
      layout: "split",
      showBusiness: true,
      bgColor: "#FFFFFF",
      textColor: "#0F172A",
      priceColor: "#1D4ED8",
      offerBgColor: "#1D4ED8",
      offerTextColor: "#FFFFFF",
      borderColor: "#BFDBFE",
    }),
  },
  {
    id: "precio",
    nombre: "Precio grande",
    descripcion: "El precio domina la etiqueta, nombre pequeño arriba",
    design: makeDesign({
      templateId: "precio",
      layout: "precioGrande",
      textSize: "grande",
      bgColor: "#FFFBEB",
      textColor: "#78350F",
      priceColor: "#B45309",
      offerBgColor: "#B45309",
      offerTextColor: "#FFFFFF",
      borderColor: "#FDE68A",
    }),
  },
  {
    id: "ficha",
    nombre: "Ficha de repisa",
    descripcion:
      "Nombre a la izquierda y precio en recuadro abajo a la derecha",
    design: makeDesign({
      templateId: "ficha",
      layout: "ficha",
      bgColor: "#FFFFFF",
      textColor: "#111827",
      priceColor: "#111827",
      offerBgColor: "#16A34A",
      offerTextColor: "#FFFFFF",
      borderColor: "#D1D5DB",
    }),
  },
  {
    id: "minimal",
    nombre: "Minimal",
    descripcion: "Alineado a la izquierda, sin borde ni separadores",
    design: makeDesign({
      templateId: "minimal",
      layout: "minimal",
      showBorder: false,
      rounded: false,
      showDivider: false,
      showBusiness: false,
      textSize: "compacto",
    }),
  },
];

export interface LabelLayoutOption {
  id: LabelLayoutId;
  nombre: string;
  descripcion: string;
}

/** Estructuras disponibles para cambiar el acomodo sin cambiar de plantilla. */
export const LABEL_LAYOUTS: LabelLayoutOption[] = [
  {
    id: "centrado",
    nombre: "Centrado",
    descripcion: "Todo centrado, precio al medio",
  },
  {
    id: "banda",
    nombre: "Banda",
    descripcion: "Precio dentro de una banda de color",
  },
  {
    id: "split",
    nombre: "Bloque",
    descripcion: "Nombre arriba y precio sobre color abajo",
  },
  {
    id: "precioGrande",
    nombre: "Precio dominante",
    descripcion: "Precio muy grande, nombre pequeño",
  },
  {
    id: "ficha",
    nombre: "Ficha",
    descripcion: "Nombre a la izquierda, precio en recuadro",
  },
  {
    id: "minimal",
    nombre: "Minimal",
    descripcion: "Alineado a la izquierda, sin adornos",
  },
];

export interface LabelPalette {
  id: string;
  nombre: string;
  bgColor: string;
  textColor: string;
  priceColor: string;
  offerBgColor: string;
  offerTextColor: string;
  borderColor: string;
}

export const LABEL_PALETTES: LabelPalette[] = [
  {
    id: "blanco",
    nombre: "Blanco",
    bgColor: "#FFFFFF",
    textColor: "#0F172A",
    priceColor: "#0F172A",
    offerBgColor: "#DC2626",
    offerTextColor: "#FFFFFF",
    borderColor: "#CBD5E1",
  },
  {
    id: "rojo",
    nombre: "Rojo oferta",
    bgColor: "#FFFFFF",
    textColor: "#7F1D1D",
    priceColor: "#DC2626",
    offerBgColor: "#DC2626",
    offerTextColor: "#FFFFFF",
    borderColor: "#FECACA",
  },
  {
    id: "azul",
    nombre: "Azul",
    bgColor: "#FFFFFF",
    textColor: "#1E3A8A",
    priceColor: "#1D4ED8",
    offerBgColor: "#1D4ED8",
    offerTextColor: "#FFFFFF",
    borderColor: "#BFDBFE",
  },
  {
    id: "verde",
    nombre: "Verde",
    bgColor: "#FFFFFF",
    textColor: "#064E3B",
    priceColor: "#059669",
    offerBgColor: "#059669",
    offerTextColor: "#FFFFFF",
    borderColor: "#A7F3D0",
  },
  {
    id: "ambar",
    nombre: "Ámbar",
    bgColor: "#FFFBEB",
    textColor: "#78350F",
    priceColor: "#B45309",
    offerBgColor: "#B45309",
    offerTextColor: "#FFFFFF",
    borderColor: "#FDE68A",
  },
  {
    id: "negro",
    nombre: "Negro",
    bgColor: "#0F172A",
    textColor: "#F8FAFC",
    priceColor: "#FACC15",
    offerBgColor: "#FACC15",
    offerTextColor: "#0F172A",
    borderColor: "#334155",
  },
];

export const LABEL_FONT_STACKS: Record<LabelFontFamily, string> = {
  sans: "'Inter', 'Segoe UI', system-ui, -apple-system, sans-serif",
  serif: "Georgia, 'Times New Roman', serif",
  mono: "'Courier New', Courier, monospace",
};

export const LABEL_SIZE_FACTORS: Record<LabelTextSize, number> = {
  compacto: 0.85,
  normal: 1,
  grande: 1.18,
};

export function designFromTemplate(id: LabelTemplateId): LabelDesign {
  const template = LABEL_TEMPLATES.find((t) => t.id === id);
  return template ? { ...template.design } : { ...DEFAULT_LABEL_DESIGN };
}

/** Completa un diseño parcial (preset antiguo) con los valores por defecto. */
export function normalizeLabelDesign(
  raw: Partial<LabelDesign> | null | undefined,
): LabelDesign {
  const merged: LabelDesign = { ...BASE_DESIGN, ...(raw ?? {}) };
  if (!LABEL_TEMPLATES.some((t) => t.id === merged.templateId)) {
    merged.templateId = "clasico";
  }
  if (!LABEL_LAYOUTS.some((l) => l.id === merged.layout)) {
    merged.layout = "centrado";
  }
  return merged;
}

interface LabelPrice {
  /** Precio final que se cobra (con descuento aplicado si existe). */
  final: number;
  /** Precio de lista antes del descuento; null si no hay descuento. */
  previous: number | null;
  hasDiscount: boolean;
  pct: number;
}

/**
 * Calcula el precio a mostrar en la etiqueta. `producto.descuento` es el % que
 * Caja y Tienda aplican sobre `precio` (ver catalogDiscount de Caja).
 */
export function resolveLabelPrice(product: LabelProduct): LabelPrice {
  const base = product.precio ?? 0;
  const pct = Math.min(100, Math.max(0, Math.round(product.descuento ?? 0)));
  if (pct <= 0 || base <= 0) {
    return { final: base, previous: null, hasDiscount: false, pct: 0 };
  }
  const final = Math.round(base * (1 - pct / 100));
  return { final, previous: base, hasDiscount: true, pct };
}
